
import React, { useState } from 'react';
import { FileStack, Sparkles, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { type CrawlResult } from '@/types/crawler';
import FeatureErrorBoundary from '../FeatureErrorBoundary';
import { useArticleRecommendations } from '@/hooks/useArticleRecommendations';
import ArticleInputForm from './ArticleInputForm';
import NoArticlesAvailable from './NoArticlesAvailable';
import RecommendationsContainer from './RecommendationsContainer';
import { API_KEYS } from '@/utils/apiKeys';

interface RecommendedArticlesWidgetProps {
  result: CrawlResult | null;
  onRetry?: () => void;
}

const RecommendedArticlesWidget: React.FC<RecommendedArticlesWidgetProps> = ({
  result,
  onRetry
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [isErrorDetailsOpen, setIsErrorDetailsOpen] = useState(false);
  const [showContextInput, setShowContextInput] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  
  // Track if context was applied to current recommendations
  const [contextApplied, setContextApplied] = useState(false);
  
  // Confirm API key is available at component level
  const apiKeyAvailable = !!API_KEYS.openai.recommended;
  
  // Initialize the hook with the feature ID
  const { 
    recommendations, 
    isLoading, 
    error, 
    fetchRecommendations,
    suggestMore,
    progress
  } = useArticleRecommendations({ 
    featureId: 'recommended',
    crawlResult: result 
  });

  const handleAnalyze = () => {
    if (!urlInput) return;
    
    // Extra validation at component level
    if (!apiKeyAvailable) {
      toast.error('API key not configured for recommendations');
      return;
    }
    
    // Reset error details visibility
    setIsErrorDetailsOpen(false);
    
    // Set flag if context is being applied
    setContextApplied(showContextInput && !!additionalContext.trim());
    
    // Show a toast explaining that context is being applied
    if (showContextInput && additionalContext.trim()) {
      toast.info(`Applying filters: "${additionalContext.trim()}"`, {
        description: "Recommendations will be filtered according to your instructions."
      });
    }
    
    // Call the fetchRecommendations method from our hook
    fetchRecommendations(urlInput, showContextInput ? additionalContext : undefined);
  };

  const handleCopyUrls = () => {
    if (!recommendations || recommendations.length === 0) {
      toast.error('No recommendations to copy');
      return;
    }
    
    const urls = recommendations.map(rec => rec.articleUrl).join('\n');
    navigator.clipboard.writeText(urls)
      .then(() => toast.success('URLs copied to clipboard'))
      .catch(err => toast.error('Failed to copy URLs: ' + err.message));
  };

  const handleDownloadCsv = () => {
    if (!recommendations || recommendations.length === 0) {
      toast.error('No recommendations to download');
      return;
    }
    
    // Create CSV content
    const headers = 'Title,URL,Similarity Score,Reasoning\n';
    const csvContent = recommendations.map(rec => {
      // Escape any commas in the text fields
      const title = `"${rec.articleTitle.replace(/"/g, '""')}"`;
      const url = `"${rec.articleUrl}"`;
      const score = Math.round(rec.similarity * 100) + '%';
      const reasoning = `"${rec.reasoning.replace(/"/g, '""')}"`;
      
      return `${title},${url},${score},${reasoning}`;
    }).join('\n');
    
    const finalCsv = headers + csvContent;
    
    // Create and trigger download
    const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'article_recommendations.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV downloaded successfully');
  };

  // Always allow the feature to be active
  const hasArticlesToRecommend = result && result.pages && result.pages.length > 0;

  // Convert progress to the expected format if it's a string
  const formattedProgress = typeof progress === 'string' 
    ? { percentage: 0, stage: progress }
    : progress;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold flex items-center gap-2">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg shadow-blue-500/20">
          <FileStack className="h-5 w-5 text-white" />
        </div>
        <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
          Recommended Articles
        </span>
        <div className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 text-blue-500">
          <span className="flex items-center">
            <Sparkles className="h-3 w-3 mr-1" />
            Smart
          </span>
        </div>
        
        {/* Show context filter badge when applied */}
        {contextApplied && recommendations && recommendations.length > 0 && (
          <div className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/20 text-green-500">
            <span className="flex items-center">
              <Filter className="h-3 w-3 mr-1" />
              Filtered
            </span>
          </div>
        )}
      </h2>
      
      <FeatureErrorBoundary componentName="Recommended Articles" onRetry={onRetry}>
        <ArticleInputForm
          urlInput={urlInput}
          setUrlInput={setUrlInput}
          showContextInput={showContextInput}
          setShowContextInput={setShowContextInput}
          additionalContext={additionalContext}
          setAdditionalContext={setAdditionalContext}
          isLoading={isLoading}
          progress={formattedProgress}
          handleAnalyze={handleAnalyze}
          hasArticlesToRecommend={hasArticlesToRecommend}
          error={error}
          isErrorDetailsOpen={isErrorDetailsOpen}
          setIsErrorDetailsOpen={setIsErrorDetailsOpen}
          apiKeyAvailable={apiKeyAvailable}
        />

        {recommendations?.length === 0 && !isLoading && !error && (
          <NoArticlesAvailable />
        )}

        {recommendations && recommendations.length > 0 && (
          <RecommendationsContainer
            recommendations={recommendations}
            handleCopyUrls={handleCopyUrls}
            handleDownloadCsv={handleDownloadCsv}
            suggestMore={suggestMore}
            isLoading={isLoading}
            hasContextFilter={contextApplied}
            contextFilter={contextApplied ? additionalContext : undefined}
          />
        )}
      </FeatureErrorBoundary>
    </div>
  );
};

export default RecommendedArticlesWidget;
